import { supabase } from "../supabase";
import { pmError } from "./errors";
import { resolveRecipients } from "./notificationRecipients";
import { propertyLabel } from "./helpers";
import { STAFF_TWIN_TYPES } from "./notificationTemplates";

// ============ NOTIFICATION QUEUE ============
// Queues email notifications for async processing by the worker
// (api/_notification-worker-impl.js). Inserting a row here does NOT
// deliver — delivery happens when the worker drains the queue.
//
// Two call modes:
//   queueNotification(type, "alice@x.com", data, cid)
//     → single-recipient legacy path. Still works for back-compat
//       with every existing call site.
//   queueNotification(type, null, data, cid)
//     → admin-driven fan-out. Reads notification_settings.custom_recipients
//       and inserts one queue row per resolved recipient with cc/bcc
//       copied onto each row.
//
// In either mode, cc and bcc from notification_settings are stamped
// onto every row we insert — admins can add a static cc to all
// payments without touching call sites.
//
// quiet_hours_start/end (in quiet_hours_tz) defer scheduled_for to
// the next end-of-window timestamp; the worker waits to send.
//
// opts.dedupKey names the event ("rent_due:lease:12:2026-11"): while a
// row with that key is waiting, being sent or sent, the database refuses
// another for the same person (uq_notification_queue_dedup), so a
// reminder is queued once however many times the check runs (LIFE-2).
export async function queueNotification(type, recipientEmail, data, companyId, opts = {}) {
  if (!companyId) return;
  try {
  // Pull every column needed for both legacy and admin-driven paths.
  const { data: settings } = await supabase.from("notification_settings")
  .select("enabled, channels, recipients, custom_recipients, cc, bcc, quiet_hours_start, quiet_hours_end, quiet_hours_tz")
  .eq("company_id", companyId).eq("event_type", type).maybeSingle();

  if (settings && !settings.enabled) return;

  // Default push to ON when no notification_settings row exists for
  // this event_type. Push delivery is safe-by-default — /api/notifications
  // ?action=push returns delivered:0 silently when the recipient has
  // no registered subscription.
  const channels = settings?.channels
  ? (typeof settings.channels === "string" ? JSON.parse(settings.channels) : settings.channels)
  : { in_app: true, email: true, push: true };

  // ── Resolve primary recipient list ─────────────────────────────
  // If the caller passed an explicit email, that's the only primary
  // recipient (legacy mode). If the caller passed null, expand
  // settings.custom_recipients via the resolver.
  let primaryRecipients = [];
  let cc = [];
  let bcc = [];
  if (recipientEmail) {
    primaryRecipients = [recipientEmail.toLowerCase()];
    // Even in legacy mode, honor the admin's cc/bcc setting.
    const resolved = await resolveRecipients(companyId, type, data, settings);
    cc = resolved.cc;
    bcc = resolved.bcc;
  } else {
    const resolved = await resolveRecipients(companyId, type, data, settings);
    primaryRecipients = resolved.primary;
    cc = resolved.cc;
    bcc = resolved.bcc;
  }
  if (primaryRecipients.length === 0) return;

  // ── Quiet hours ────────────────────────────────────────────────
  // If we're inside the configured window, defer to next end-of-window.
  const scheduledFor = computeScheduledFor(settings);

  // ── Queue email rows ───────────────────────────────────────────
  if (channels.email) {
    // The data as an object (jsonb), never a JSON string: a string made
    // every data->>'key' lookup miss (LIFE-2).
    const dataObj = typeof data === "string" ? (() => { try { return JSON.parse(data); } catch (_e) { return { text: data }; } })() : (data || {});
    // A staff member gets the staff wording when the type has one
    // (late_fee_applied -> late_fee_applied_admin): the tenant's "Hi
    // {{tenant}}, your account ..." went to admins as well (LIFE-18).
    let staff = new Set();
    if (STAFF_TWIN_TYPES.has(type)) {
      const { data: mems } = await supabase.from("company_members").select("user_email, role")
        .eq("company_id", companyId).eq("status", "active");
      staff = new Set((mems || []).filter(m => m.role !== "tenant" && m.role !== "owner").map(m => String(m.user_email || "").toLowerCase()));
    }
    const rows = primaryRecipients.map(rcpt => ({
      company_id: companyId,
      type: staff.has(rcpt) ? type + "_admin" : type,
      recipient_email: rcpt,
      data: dataObj,
      status: "pending",
      cc: cc.length ? cc : [],
      bcc: bcc.length ? bcc : [],
      scheduled_for: scheduledFor,
      dedup_key: opts.dedupKey || null,
    }));
    const insErr = await insertQueueRows(rows);
    if (insErr) pmError("PM-8006", { raw: insErr, context: "email queue insert", silent: true });
    else {
      // Fire-and-forget the worker so non-deferred rows drain now.
      // Deferred rows (scheduled_for > now) wait until the cron tick
      // OR the next user-triggered worker call after the window.
      if (!scheduledFor) {
        try {
          const { data: session } = await supabase.auth.getSession();
          const jwt = session?.session?.access_token;
          if (jwt) {
            fetch("/api/notifications?action=worker", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: "Bearer " + jwt },
              body: JSON.stringify({}),
            }).catch(err => pmError("PM-8006", { raw: err, context: "worker trigger", silent: true }));
          }
        } catch (e) { pmError("PM-8006", { raw: e, context: "worker dispatch", silent: true }); }
      }
    }
  }

  // ── Push delivery ──────────────────────────────────────────────
  // One push per primary recipient. cc/bcc are EMAIL semantics — they
  // don't fan out to push (a push notification is a one-recipient
  // concept; cc/bcc to push would be confusing and noisy).
  if (channels.push) {
    try {
      const { data: session } = await supabase.auth.getSession();
      const jwt = session?.session?.access_token;
      if (jwt) {
        const title = pushTitleFor(type, data);
        const message = pushBodyFor(type, data);
        const url = pushUrlFor(type);
        await Promise.all(primaryRecipients.map(rcpt =>
          fetch("/api/notifications?action=push", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: "Bearer " + jwt },
            body: JSON.stringify({ company_id: companyId, user_email: rcpt, title, body: message, url }),
          }).catch(err => pmError("PM-8006", { raw: err, context: "push fetch", silent: true }))
        ));
      }
    } catch (e) { pmError("PM-8006", { raw: e, context: "push dispatch", silent: true }); }
  }
  } catch (e) { pmError("PM-8006", { raw: e, context: "queue notification", silent: true }); }
}

// Insert queue rows; a row refused as a duplicate (the same event for the
// same person already queued or sent) is simply not queued again.
// Returns the first real error, or null.
export async function insertQueueRows(rows) {
  if (!rows.length) return null;
  const { error } = await supabase.from("notification_queue").insert(rows);
  if (!error) return null;
  if (error.code !== "23505") return error;
  for (const row of rows) {
    const { error: one } = await supabase.from("notification_queue").insert(row);
    if (one && one.code !== "23505") return one;
  }
  return null;
}

// The wall-clock parts of an instant in a time zone.
function partsIn(tz, at) {
  const fmt = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  const p = Object.fromEntries(fmt.formatToParts(at).map(x => [x.type, x.value]));
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute };
}
// The instant at which the clock in `tz` reads y-mo-d h:mi. The zone's
// offset is measured at a first guess and corrected once, which also
// lands correctly on the days the clocks change.
export function zonedInstant(tz, y, mo, d, h, mi) {
  const want = Date.UTC(y, mo - 1, d, h, mi);
  let t = want;
  for (let i = 0; i < 2; i++) {
    const p = partsIn(tz, new Date(t));
    t += want - Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi);
  }
  return new Date(t);
}

// scheduled_for for the quiet-hours window: null outside it (send now);
// inside it, the instant the window ends in the COMPANY's time zone.
// It used to read the end time as UTC -- 4 to 5 hours early for Eastern,
// often already past, so messages went out inside quiet hours (LIFE-5).
// Handles windows that wrap midnight (22:00 -> 07:00).
export function computeScheduledFor(settings, now = new Date()) {
  if (!settings?.quiet_hours_start || !settings?.quiet_hours_end) return null;
  const tz = settings.quiet_hours_tz || "America/New_York";
  let p;
  try { p = partsIn(tz, now); } catch (_e) { return null; }
  const nowMins = p.h * 60 + p.mi;
  const startMins = toMins(settings.quiet_hours_start);
  const endMins = toMins(settings.quiet_hours_end);
  if (startMins === endMins) return null;
  const inWindow = startMins < endMins
    ? (nowMins >= startMins && nowMins < endMins)
    : (nowMins >= startMins || nowMins < endMins);
  if (!inWindow) return null;
  // The window ends today, or -- when it wraps and we are before midnight -- tomorrow.
  let end = zonedInstant(tz, p.y, p.mo, p.d, Math.floor(endMins / 60), endMins % 60);
  if (end <= now) {
    const next = new Date(Date.UTC(p.y, p.mo - 1, p.d) + 86400000);
    end = zonedInstant(tz, next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), Math.floor(endMins / 60), endMins % 60);
  }
  return end.toISOString();
}

function toMins(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = String(timeStr).split(":").map(n => parseInt(n, 10) || 0);
  return h * 60 + m;
}

// Human-readable title/body per notification type. Kept terse — push
// banners truncate hard on mobile, so the first ~40 chars carry the
// signal and the full text lives in the in-app/email copy.
//
// Title carries the WHO + WHERE context (sender · short property
// address) so the recipient can triage without opening the app.
// Body carries the actual content (the message, the amount, etc.).
// Strip the property to its first comma-separated segment so
// "10013 Dakin Ct, Cheltenham, MD 20623" shows as "10013 Dakin Ct"
// — full address won't fit on a phone banner anyway.
function shortProp(p) {
  if (!p || typeof p !== "string") return "";
  return propertyLabel(p);
}
function pushTitleFor(type, data) {
  const d = typeof data === "string" ? {} : (data || {});
  const who = d.sender || d.tenant || "";
  const where = shortProp(d.property);
  const join = (a, b) => [a, b].filter(Boolean).join(" \u00b7 "); // " · "
  if (type === "message_received") return join(who, where) || "New message";
  if (type === "payment_received") return join(who, where) || "Payment received";
  if (type === "move_out") return join(d.tenant, where) || "Move-out completed";
  if (type === "maintenance_request") return join(d.tenant, where) || "Maintenance request";
  const map = {
    deposit_returned: "Deposit returned",
    lease_expiry: "Lease expiring soon",
    work_order_update: "Work order update",
    invoice_approved: "Invoice approved",
    invoice_rejected: "Invoice rejected",
    approval_request: "Approval needed",
    document_uploaded: "Document uploaded",
  };
  return map[type] || "Housify";
}
function pushBodyFor(type, data) {
  const d = typeof data === "string" ? {} : (data || {});
  if (type === "message_received") return (d.preview || "").slice(0, 140) || "(no preview)";
  if (type === "payment_received") return "$" + (d.amount ?? "?") + (d.status ? " · " + d.status : "");
  if (type === "move_out") return "Move-out " + (d.moveOutDate || "completed");
  if (type === "deposit_returned") return "$" + (d.returned ?? 0) + " returned to " + (d.tenant || "tenant");
  if (type === "maintenance_request") return d.title || d.preview || "New request";
  return d.preview || d.message || d.description || "";
}
function pushUrlFor(type) {
  if (type === "message_received") return "/#messages";
  if (type === "payment_received") return "/#payments";
  if (type === "maintenance_request" || type === "work_order_update") return "/#maintenance";
  if (type === "approval_request") return "/#approvals";
  return "/";
}
